import { type DocumentData, type DocumentSnapshot, doc } from 'firebase/firestore'

import { getFirebaseFirestore } from '@/lib/firebase/app'
import { callable } from '@/lib/firebase/callable'
import { type Subscription, subscribeDoc } from '@/lib/firebase/subscribe'
import type { SetTourStateInput, SetTourStateResult, TourState } from '@/types/tour'

export const EMPTY_TOUR_STATE: TourState = { seen: [], skipped: [], tipsEnabled: true }

function asIdList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string')
    : []
}

/** Maps the user document's `tours` field; a user doc with no tour state resolves to a usable default, only a missing doc reads as absent. */
function toTourState(snapshot: DocumentSnapshot<DocumentData>): TourState | undefined {
  const data = snapshot.data()
  if (!data) return undefined

  const tours = (typeof data.tours === 'object' && data.tours !== null ? data.tours : {}) as Record<
    string,
    unknown
  >

  return {
    seen: asIdList(tours.seen),
    skipped: asIdList(tours.skipped),
    tipsEnabled: typeof tours.tipsEnabled === 'boolean' ? tours.tipsEnabled : true,
    handoffAt: typeof tours.handoffAt === 'string' && tours.handoffAt ? tours.handoffAt : undefined,
  }
}

export function subscribeTourState(uid: string): Subscription<TourState> {
  return subscribeDoc(doc(getFirebaseFirestore(), 'users', uid), toTourState)
}

export const setTourState = callable<SetTourStateInput, SetTourStateResult>('setTourState')
